import React, { useMemo, useState } from "react";

import { DEVICE_CATALOG } from "../deviceCatalog";
import { DeviceCheckStep } from "../StepDeviceCheck.types";
import { sanitizeContent as s } from "../../../../util/functions";
import block from "bem-css-modules";
import styles from "../StepDeviceCheck.module.scss";

const DEVICE_TYPES = Object.keys(DEVICE_CATALOG);

const Screen1BManual = (props: {
  stepData: DeviceCheckStep;
  showDetectionFailedBanner: boolean;
  onConfirm: (os: string, osVersion: string, model: string) => void;
}) => {
  const { stepData, showDetectionFailedBanner, onConfirm } = props;
  const b = block(styles);

  const [deviceType, setDeviceType] = useState("");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [osVersion, setOsVersion] = useState("");

  const entry = deviceType ? DEVICE_CATALOG[deviceType] : null;
  const brands = useMemo(() => (entry ? Object.keys(entry.models) : []), [entry]);
  const models = useMemo(() => (entry && brand ? entry.models[brand] || [] : []), [entry, brand]);
  const versions = useMemo(() => (entry ? entry.versions : []), [entry]);

  const os = entry?.os || deviceType;
  const isOther = deviceType === "Other";
  const canSubmit = isOther || (deviceType && model && osVersion);

  const helpText = useMemo(() => {
    if (os === "iOS") return stepData.iosHelpText;
    if (os === "Android") return stepData.androidHelpText;
    return "";
  }, [os, stepData.iosHelpText, stepData.androidHelpText]);

  const handleSubmit = () => {
    if (!canSubmit) return;
    const familyLabel = brand && model ? `${brand} ${model}` : model || brand || deviceType;
    onConfirm(os || "Other", osVersion || "0", familyLabel);
  };

  return (
    <div className={b("screen")}>
      {showDetectionFailedBanner && (
        <div className={b("banner")}>{stepData.detectionFailedText}</div>
      )}
      {stepData.manualTitle && <h3 className={b("title")}>{stepData.manualTitle}</h3>}
      {stepData.manualBody && (
        <p className={b("body")} dangerouslySetInnerHTML={{ __html: s(stepData.manualBody) }} />
      )}

      <label className={b("field-label")}>{stepData.osLabel}</label>
      <select
        className={b("select")}
        value={deviceType}
        onChange={(e) => {
          setDeviceType(e.target.value);
          setBrand("");
          setModel("");
          setOsVersion("");
        }}
      >
        <option value="">—</option>
        {DEVICE_TYPES.map((dt) => (
          <option key={dt} value={dt}>{dt}</option>
        ))}
        <option value="Other">{stepData.otherOptionLabel}</option>
      </select>

      {deviceType && !isOther && (
        <>
          <label className={b("field-label")}>{stepData.modelLabel}</label>
          <select
            className={b("select")}
            value={brand}
            onChange={(e) => {
              setBrand(e.target.value);
              setModel("");
            }}
          >
            <option value="">—</option>
            {brands.map((br) => (
              <option key={br} value={br}>{br}</option>
            ))}
          </select>
        </>
      )}

      {brand && (
        <>
          <label className={b("field-label")}>{stepData.modelPlaceholder}</label>
          <select
            className={b("select")}
            value={model}
            onChange={(e) => setModel(e.target.value)}
          >
            <option value="">—</option>
            {models.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </>
      )}

      {deviceType && !isOther && (
        <>
          <label className={b("field-label")}>{stepData.versionLabel}</label>
          <select
            className={b("select")}
            value={osVersion}
            onChange={(e) => setOsVersion(e.target.value)}
          >
            <option value="">—</option>
            {versions.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </>
      )}

      {helpText && <p className={b("help-text")}>{helpText}</p>}

      <button
        type="button"
        className={b("primary-cta")}
        onClick={handleSubmit}
        disabled={!canSubmit}
      >
        {stepData.confirmButtonText}
      </button>
    </div>
  );
};

export default Screen1BManual;
